import React, { useCallback, useContext, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Spinner } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import Form from 'react-bootstrap/Form';
import {
  isKitProduct,
  showToast,
  updateProductStockInProductList,
} from '../../config_and_helpers/helpers';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { CheckLg } from 'react-bootstrap-icons';
import {
  getProductDetails,
  getProductSelectList,
  updateProductStock,
} from '../../clients/product_client';
import DropdownSelect from '../Generic/DropdownSelect';
import CustomProductContextHook from '../../pages/Product/CustomProductContextHook';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { refreshParentKitsInProductList } from '../../utils/kitStockLiveRefresh';

const LOG_CONTEXT = 'UpdateAmountModal';

const KIT_STOCK_DERIVED_COPY =
  'Kit count follows component inventory. Edit component stock instead of the kit package count.';

/**
 * Represents a modal component for updating the stock amount of a product.
 *
 * This component offers a user interface for increasing or decreasing the stock level of a product.
 * It includes validation to prevent setting a negative stock level. The component can operate in two modes:
 * standalone or integrated within a larger component (like a home page), determined by the 'homeShowModal' prop.
 *
 * Kit products: package count is derived from BOM components. The inventory
 * embed hides this control; Home still lists kits — submit is blocked and a
 * derived API response never writes the typed amount into local stock.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.product - The product object containing details like current stock and product ID.
 * @param {Function} props.onSuccessfulUpdate - Callback function to execute after a successful stock update.
 * @param {boolean} [props.homeShowModal] - Flag to control the visibility of the modal in integrated mode.
 * @param {Function} [props.setHomeShowModal] - Function to set the visibility state of the modal in integrated mode.
 * @returns The UpdateAmountModal component.
 *
 * Usage:
 * ```jsx
 * <UpdateAmountModal
 *    product={productDetails}
 *    onSuccessfulUpdate={updateHandler}
 *    homeShowModal={showModalFlag}
 *    setHomeShowModal={setShowModalFlag}
 * />
 * ```
 */
const UpdateAmountModal = ({
  product,
  onSuccessfulUpdate,
  homeShowModal,
  setHomeShowModal,
}) => {
  // Custom hook returning the products array via the ProductContext or falling back on generic, empty data
  const { products, setProducts } = CustomProductContextHook();
  // Context to access the application-wide token.
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  // State management for various component functionalities.
  const [showModal, setShowModal] = useState(
    homeShowModal ? homeShowModal : false
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amount, setAmount] = useState('');
  const [productSelectList, setProductSelectList] = useState([]);
  const [fetchedProduct, setFetchedProduct] = useState(null);
  const [isInputNegative, setIsInputNegative] = useState(false);

  const selectedProduct = fetchedProduct || product;
  const selectedIsKit = isKitProduct(selectedProduct);

  // Function to fetch a list of products for the dropdown.
  const fetchProductSelectList = useCallback(async () => {
    await getProductSelectList(token, setProductSelectList);
  }, [token]);

  // Function to fetch details of a specific product.
  const fetchProduct = async selected => {
    setAmount('');
    setIsInputNegative(false);
    await getProductDetails(token, selected.value, setFetchedProduct);
  };

  // useEffect to fetch the product select list only when in home mode (when dropdown is needed).
  useEffect(() => {
    if (homeShowModal) {
      fetchProductSelectList();
    }
  }, [homeShowModal, token, fetchProductSelectList]);

  useEffect(() => {
    if (fetchedProduct && isKitProduct(fetchedProduct)) {
      logInfo(LOG_CONTEXT, 'kit selected; stock edit blocked', {
        productId: fetchedProduct.id,
        stock: fetchedProduct.stock,
        effectiveStock: fetchedProduct.effective_stock,
      });
    }
  }, [fetchedProduct]);

  // Function to close the modal.
  const handleClose = () => {
    if (setHomeShowModal) setHomeShowModal(false);
    setShowModal(false);
  };

  // Function to open the modal.
  const handleShow = () => setShowModal(true);

  // Function to handle submission of stock update.
  function handleSubmit() {
    const productToUse = fetchedProduct || product;
    const productIdToUse = fetchedProduct
      ? fetchedProduct?.id
      : product?.productId ?? product?.id;

    if (isKitProduct(productToUse)) {
      logInfo(LOG_CONTEXT, 'blocked kit update_product_stock submit', {
        productId: productIdToUse,
      });
      showToast(KIT_STOCK_DERIVED_COPY, 'info', 'top-right', 4000);
      return;
    }

    setIsSubmitting(true);

    // API call to update the product stock.
    updateProductStock(token, productIdToUse, amount, productToUse).then(
      response => {
        if (response && response.success) {
          handleClose();
          const isDerived = Boolean(response.stockDerivedFromComponents);
          const updatedItems = response?.updatedItems;
          const newItems = response?.newItems;

          // Derived kit re-sync may return `updated_items: []`. Do not replace
          // existing lots with an empty list; persist response stock instead.
          const hasLotPayload = isDerived
            ? Array.isArray(updatedItems) && updatedItems.length > 0
            : true;
          const stockItemArray = hasLotPayload
            ? updatedItems || newItems || []
            : undefined;
          const isAppendMode = !isDerived && !!newItems;

          const resolvedAmount =
            response.stock !== undefined && response.stock !== null
              ? response.stock
              : amount;

          logInfo(LOG_CONTEXT, 'update_product_stock applied', {
            productId: productIdToUse,
            isDerived,
            requestedAmount: amount,
            resolvedStock: resolvedAmount,
            effectiveStock: response.effectiveStock,
            lotCount: Array.isArray(stockItemArray)
              ? stockItemArray.length
              : null,
          });

          if (onSuccessfulUpdate && stockItemArray) {
            onSuccessfulUpdate({
              data: stockItemArray,
              replaceItems: !isAppendMode,
            });
          }

          const nextList = updateProductStockInProductList({
            products,
            productId: productIdToUse,
            operation: null,
            manualCreationValue: null,
            updatedAmount: resolvedAmount,
            effectiveStock: response.effectiveStock,
            stockItemArray: stockItemArray,
            isAppendMode: isAppendMode,
          });
          setProducts(nextList);

          if (Array.isArray(nextList) && nextList.length > 0) {
            refreshParentKitsInProductList({
              token,
              products: nextList,
              componentId: productIdToUse,
            })
              .then(({ products: withKits, failedIds }) => {
                setProducts(withKits);
                if (failedIds.length) {
                  logError(LOG_CONTEXT, 'parent kit refresh failed', {
                    componentId: productIdToUse,
                    failedIds,
                  });
                  showToast(
                    'Kit complete-kit count could not be refreshed. Reopen the kit to see updated lots.',
                    'warning',
                    'top-right',
                    4000
                  );
                }
              })
              .catch(err => {
                logError(LOG_CONTEXT, 'parent kit refresh error', {
                  componentId: productIdToUse,
                  message: err?.message ?? null,
                });
              });
          }

          response.toast();
        } else {
          showToast(
            'An unexpected error occurred. Please try again in a little while.',
            'error',
            'top-right',
            3000
          );
        }
        setIsSubmitting(false);
      }
    );
  }

  const canConfirm = selectedIsKit
    ? false
    : Boolean(amount) && !isInputNegative && (!homeShowModal || fetchedProduct);

  if (isViewer) {
    return null;
  }

  return (
    <div>
      {/* If accessed from the Inventory Detail view, this input group for stock adjustment will appear. */}
      {!homeShowModal && (
        <InputGroup>
          <Form.Control
            style={{ textAlign: 'center' }}
            value={amount}
            placeholder="Updated Amount"
            isInvalid={isInputNegative}
            onChange={e => {
              const inputValue = Number(e.target.value);
              // Check if the entered value is negative
              if (inputValue < 0) {
                setIsInputNegative(true);
              } else {
                setIsInputNegative(false);
              }
              setAmount(e.target.value);
            }}
          />

          {/* Button to execute the stock update. Disabled if the input is negative */}
          <Button
            disabled={!amount || isInputNegative}
            variant="outline-success"
            onClick={() => {
              handleShow();
            }}
            className="rounded-edge-button-right"
          >
            <CheckLg size={25} />
          </Button>

          {/* Negative feedback if the number entered is negative */}
          <Form.Control.Feedback type="invalid">
            Number cannot be negative!
          </Form.Control.Feedback>
        </InputGroup>
      )}

      {/* Modal for updating stock amount. */}
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Update Stock</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* If accessed viww the Inventory Detail view, a confirmation message will appear. */}
          {!homeShowModal && (
            <h4>
              Update {product.catNum}'s stock from {product.currentStock} to{' '}
              {amount}?
            </h4>
          )}

          {/* If accessed via the home page's quick actions, a dropdown menu of the lab's products will appear. */}
          <div className="d-flex flex-column align-items-center text-center">
            {homeShowModal ? (
              <DropdownSelect
                optionsList={productSelectList}
                label="Product"
                selectedValue={productSelectList.find(
                  p => p.value === fetchedProduct?.id
                )}
                setSelectedValue={fetchProduct}
                disabledOptions={[]}
              />
            ) : null}

            {/* Once the product is chosen, the input field to enter the updates stock and confirmation button will appear */}
            {fetchedProduct && (
              <>
                <div className="d-flex flex-row align-items-center mt-3">
                  <h6 className="mt-1">Current stock:</h6>
                  <p className="ms-2 mb-1">{fetchedProduct.stock}</p>
                </div>
                {isKitProduct(fetchedProduct) ? (
                  <p className="text-muted mt-3 mb-2">{KIT_STOCK_DERIVED_COPY}</p>
                ) : (
                  <Form.Group className="d-flex flex-column justify-content-center mt-3 mb-2">
                    <Form.Label>Updated Amount</Form.Label>
                    <Form.Control
                      id="amountInput"
                      style={{ textAlign: 'center' }}
                      className="w-50 mx-auto"
                      value={amount}
                      placeholder="Updated Amount"
                      onChange={e => setAmount(e.target.value)}
                    />
                  </Form.Group>
                )}
              </>
            )}
          </div>
        </Modal.Body>

        {/* Modal footer with action buttons. */}
        <Modal.Footer>
          {isSubmitting ? (
            <Button variant="danger" disabled>
              <Spinner
                size="sm"
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
              />
            </Button>
          ) : (
            <Button
              variant="success"
              disabled={!canConfirm}
              onClick={e => {
                handleSubmit(e);
              }}
            >
              Confirm
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>
            Oops!
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default UpdateAmountModal;
